import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  cabinetPieceAxes,
  cabinetPieceInsertIndex,
  catalogCabinetAttributes,
  deriveCabinetDimensionsFromPieces,
  drawerGeneratedName,
} from '@kreart/shared';
import type {
  CabinetPieceInput,
  CabinetPieceKindValue,
  CabinetPieceListItem,
  CatalogCabinetAttributes,
  CatalogItemDetails,
  CatalogHardwareListItem,
  CabinetDetails,
  CabinetListItem,
  CreateCabinetFromCatalogRequest,
  CreateCabinetRequest,
  CreateCatalogItemRequest,
  CreateDrawerRequest,
  DrawerListItem,
  MaterialListItem,
  ParametricCabinetSpec,
  PreviewCabinetPiecesRequest,
  PreviewCabinetRequest,
  UpdateCabinetRequest,
} from '@kreart/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, TextField, SelectField, FormErrorBanner } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { CatalogItemPicker } from '@/features/project-items/shared/CatalogItemPicker';
import { useSaveCatalogBlueprint } from '@/features/project-items/shared/useSaveCatalogBlueprint';
import { copyName } from '@/features/project-items/shared/copyName';
import { skuForCopy } from '@/features/project-items/shared/copySku';
import { useCatalogItem } from '@/features/catalog/hooks';
import {
  EdgeAutosuggest,
  HardwareAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { BreakdownPanel } from '@/features/pricing/components/BreakdownPanel';
import {
  useCatalogCoefficientPrompt,
  useCoefficientTarget,
} from '@/features/catalog/components/SaveCatalogCoefficientPrompt';
import { CoefficientField } from '@/features/project-items/shared/CoefficientField';
import { usePreviewCabinet, usePreviewCabinetPieces } from '@/features/pricing/hooks';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { useRooms } from '@/features/projects/rooms/hooks';
import { RoomAreaFields } from '@/features/projects/areas/components/RoomAreaFields';
import { AddDrawerDialog } from '@/features/project-items/drawers/components/AddDrawerDialog';
import { useDeleteDrawer, useDrawers } from '@/features/project-items/drawers/hooks';
import { ConfirmDialog } from '@/features/users/components/ConfirmDialog';
import {
  useCabinet,
  useCreateCabinet,
  useCreateCabinetFromCatalog,
  useUpdateCabinet,
} from '../hooks';
import { errorKeyFor } from '../errorMessages';
import { useAutoSelectedEdge } from '@/features/project-items/shared/useAutoSelectedEdge';
import { useDefaultMaterialId } from '@/features/project-items/shared/useDefaultMaterial';
import { CabinetDoubledCheckboxes } from '@/features/project-items/shared/CabinetDoubledCheckboxes';
import {
  dedupeHardwareItems,
  hardwareItemsWithReplacement,
  HardwareItemRow,
} from '@/features/project-items/shared/CabinetHardwareItemRow';
import {
  CabinetPieceTable,
  cutListHasUserInput,
  defaultPieceRows,
  duplicatedPieceRow,
  makePieceRow,
  nextPieceNumber,
  pieceRowSchema,
  pieceRowsFromParametric,
  type ParametricPieceMaterials,
  type PieceRowValues,
} from './CabinetPieceTable';

interface Props {
  projectId: string;
  defaultRoomId?: string;
  cabinet?: CabinetListItem | null;
  /**
   * `copy` pre-fills every field from `cabinet` but saves through the create
   * path, so the source row is only ever read.
   */
  mode?: 'edit' | 'copy';
  open: boolean;
  onClose: () => void;
}

const decimalRegex = /^\d+(\.\d+)?$/;
const decimalString = z.string().regex(decimalRegex);
const positiveDecimalString = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});
const positiveIntegerString = z
  .string()
  .regex(/^\d+$/, 'Expected positive integer')
  .refine((v) => parseInt(v, 10) > 0, { message: 'Must be greater than 0' });
const requiredCatalogId = z.string().min(1, 'Choose a value from the suggestions');
const HARDWARE_PREVIEW_LIMIT = 3;
const DRAWER_PREVIEW_LIMIT = 3;

/**
 * Drawers in the order they sit in the cabinet, top first — the order their
 * generated names describe, so a list reading "Sus, Mijloc, Jos" runs downwards
 * like the stack it represents. The API already returns them this way; sorting
 * again keeps the rows right if a cached response ever arrives out of order.
 */
function sortByPosition<T extends { orderIndex: number; createdAt: string; id: string }>(
  items: readonly T[],
) {
  return [...items].sort((a, b) => {
    const orderDiff = a.orderIndex - b.orderIndex;
    if (orderDiff !== 0) return orderDiff;
    const createdDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (createdDiff !== 0) return createdDiff;
    return a.id.localeCompare(b.id);
  });
}

const selectedHardwareSchema = z.object({
  hardwareCatalogId: requiredCatalogId,
  quantity: positiveIntegerString,
  lengthM: z
    .string()
    .regex(/^\d*(\.\d+)?$/)
    .default('0'),
  coefficient: positiveDecimalString,
  name: z.string().optional(),
  sku: z.string().nullable().optional(),
  manufacturer: z.string().nullable().optional(),
  pricePerPiece: decimalString.optional(),
  currency: z.string().optional(),
});

// One form object backs both the Custom and "Pe piese" tabs — they share the
// room/name, hardware, drawers, quantity, coefficient and pricing preview, so
// splitting them into two `useForm`s would duplicate ~300 lines of wiring.
// The per-mode rules live in the `superRefine` below instead of on the fields.
const manualObjectSchema = z.object({
  roomId: z.string().optional(),
  areaId: z.string().optional(),
  name: z.string().trim().min(1).max(200),
  // Kept in step with create/updateCabinetRequest's `optionalTrim(50)`. No input
  // renders this field — it only ferries the source row's SKU through — so a
  // value that fails here is unfixable from the UI; seed it through `skuForCopy`
  // (see manualValuesFromCabinet) rather than letting it land over-cap.
  sku: z.string().trim().max(50).optional(),
  // Relaxed to min(0): a PIECES cabinet derives these from its cut list and the
  // fields are not rendered. The PARAMETRIC branch of superRefine still requires
  // them to be > 0.
  widthMm: z.coerce.number().int().min(0).max(100_000),
  heightMm: z.coerce.number().int().min(0).max(100_000),
  depthMm: z.coerce.number().int().min(0).max(100_000),
  intermediarCount: z.coerce.number().int().min(0).max(10_000),
  politeCount: z.coerce.number().int().min(0).max(10_000),
  intermediarDoubled: z.boolean().default(false),
  carcassDoubled: z.boolean().default(false),
  carcassExteriorFront: z.boolean().default(false),
  politeDoubled: z.boolean().default(false),
  quantity: positiveIntegerString,
  coefficient: positiveDecimalString,
  // One markup per board. The cabinet-level `coefficient` above is kept for the
  // Preț manual tab, which has no materials to hang a number on.
  carcassCoefficient: positiveDecimalString,
  frontCoefficient: positiveDecimalString,
  backCoefficient: positiveDecimalString,
  unitPriceNet: decimalString,
  notes: z.string().trim().max(2000).optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']).default('AUTO'),
  // Relaxed for the same reason as the dimensions above.
  materialId: z.string(),
  edgeId: z.string(),
  frontMaterialId: z.string(),
  frontEdgeId: z.string(),
  backMaterialId: z.string(),
  hardwareCatalogId: z.string().optional(),
  hardwareQuantity: positiveIntegerString,
  hardwareItems: z.array(selectedHardwareSchema).default([]),
  joinTypeKind: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']),
  constructionMode: z.enum(['PARAMETRIC', 'PIECES']).default('PARAMETRIC'),
  pieces: z.array(pieceRowSchema).default([]),
  // Transient picker state for the cut list's "apply to every piece" bar —
  // never submitted, like hardwareCatalogId/hardwareQuantity above.
  piecesBulkMaterialId: z.string().default(''),
  piecesBulkEdgeId: z.string().default(''),
});

// Messages shaped like an i18n key (a dot, no spaces) are auto-translated by the
// form primitives — see docs/conventions/forms.md.
const manualSchema = manualObjectSchema.superRefine((values, ctx) => {
  // A cut list is stored whichever mode the cabinet is in, so it has to be valid
  // whichever mode the cabinet is in. The untouched scaffolding a Custom cabinet
  // carries is exempt — that is exactly what `cutListHasUserInput` recognises.
  if (values.constructionMode === 'PIECES' || cutListHasUserInput(values.pieces ?? [])) {
    // Only a cabinet PRICED from its pieces needs at least one; a Custom one may
    // legitimately have none.
    if (values.constructionMode === 'PIECES' && values.pieces.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pieces'],
        message: 'projectItems.cabinets.piecesTable.errors.atLeastOne',
      });
    }
    // Rows are validated here rather than in pieceRowSchema, so the seeded blank
    // rows never block a Custom-tab submit where the table isn't even shown.
    values.pieces.forEach((piece, index) => {
      if (!piece.materialId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pieces', index, 'materialId'],
          message: 'projectItems.cabinets.piecesTable.errors.materialRequired',
        });
      }
      for (const key of ['lengthMm', 'widthMm'] as const) {
        if (!(Number(piece[key]) > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pieces', index, key],
            message: 'Must be greater than 0',
          });
        }
      }
      // A row stands for at least one board; 0 would price it away silently.
      if (!(Number(piece.quantity) >= 1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pieces', index, 'quantity'],
          message: 'projectItems.cabinets.piecesTable.errors.quantityMin',
        });
      }
    });
    return;
  }
  if (!values.materialId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['materialId'],
      message: 'Choose a value from the suggestions',
    });
  }
  for (const key of ['widthMm', 'heightMm', 'depthMm'] as const) {
    if (!(values[key] > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: 'Must be greater than 0',
      });
    }
  }
});
// Infer from the object, not the ZodEffects — `.extend`/`.omit` are unavailable
// on the refined schema.
type ManualValues = z.infer<typeof manualObjectSchema>;

const fromCatalogSchema = z.object({
  catalogItemId: z.string().min(1),
  roomId: z.string().optional(),
  areaId: z.string().optional(),
  unitPriceNet: decimalString,
  quantity: positiveIntegerString,
  coefficient: positiveDecimalString,
  notes: z.string().trim().max(2000).optional(),
});
type FromCatalogValues = z.infer<typeof fromCatalogSchema>;

/**
 * "Pret manual" — a lump-sum line: a name and the figure it sells for, with
 * none of the construction inputs. Attach one to a room to price that whole
 * room in a single row, or leave the room empty to price the whole project.
 *
 * Deliberately no quantity/coefficient field: the number typed here IS the
 * line total. See submitManualPrice for why both are pinned on submit.
 */
const manualPriceSchema = z.object({
  roomId: z.string().optional(),
  areaId: z.string().optional(),
  name: z.string().trim().min(1).max(200),
  unitPriceNet: decimalString,
});
type ManualPriceValues = z.infer<typeof manualPriceSchema>;

function manualPriceDefaults(defaultRoomId?: string): ManualPriceValues {
  return { roomId: defaultRoomId ?? '', areaId: '', name: '', unitPriceNet: '0.00' };
}

function manualPriceValuesFromCabinet(
  cabinet: CabinetListItem | CabinetDetails,
  defaultRoomId?: string,
): ManualPriceValues {
  return {
    roomId: cabinet.roomId ?? defaultRoomId ?? '',
    areaId: cabinet.areaId ?? '',
    name: cabinet.name,
    // Absent for callers without PROJECTS_READ_FINANCIALS.
    unitPriceNet: cabinet.unitPriceNet ?? '0.00',
  };
}

/**
 * Recognise a lump-sum row structurally: priced by hand AND carrying no
 * geometry. Checking `pricingMode` alone would be wrong — an AUTO cabinet whose
 * pricing failed is stored as MANUAL but keeps its dimensions and materials, and
 * must stay on the full form where those are still editable.
 */
export function isManualPriceCabinet(
  cabinet: Pick<CabinetListItem, 'pricingMode' | 'widthMm' | 'heightMm' | 'depthMm'>,
): boolean {
  return (
    cabinet.pricingMode === 'MANUAL' &&
    cabinet.widthMm === 0 &&
    cabinet.heightMm === 0 &&
    cabinet.depthMm === 0
  );
}

/** `manual` is the Custom (Personalizat) form; `price` is the lump-sum one. */
type CabinetTab = 'catalog' | 'pieces' | 'manual' | 'price';

type DrawerDraft = {
  id: string;
  body: CreateDrawerRequest;
};

/**
 * What each unsaved drawer draft will be called, keyed by draft id.
 *
 * A draft whose mode generates its name has no name yet — the server names the
 * whole cabinet on save — so the list would otherwise fall back to a bare
 * "Sertar" for every row. The name follows the draft's index in the *full*
 * list, which is the orderIndex it will be given: drafts are submitted in array
 * order and `createDrawerDrafts` numbers them 0, 1, 2 from there.
 *
 * Keyed by id rather than returned positionally because the rows render a
 * truncated slice of this list.
 */
export function drawerDraftDisplayNames(
  drafts: readonly { id: string; body: Pick<CreateDrawerRequest, 'name' | 'nameMode'> }[],
): Map<string, string> {
  return new Map(
    drafts.map((draft, index) => [
      draft.id,
      drawerGeneratedName(draft.body.nameMode ?? 'MANUAL', index, drafts.length) ||
        draft.body.name ||
        'Sertar',
    ]),
  );
}

/**
 * Where a drawer sits in its cabinet, for previewing a generated name. A new
 * drawer is appended, so it takes the slot after the last existing one.
 */
export function drawerStackPosition(
  drawers: DrawerListItem[] | undefined,
  editing: DrawerListItem | null,
): { index: number; total: number } {
  const list = drawers ?? [];
  if (!editing) return { index: list.length, total: list.length + 1 };
  return {
    index: Math.max(
      0,
      list.findIndex((drawer) => drawer.id === editing.id),
    ),
    total: list.length,
  };
}

function pieceRowFromListItem(piece: CabinetPieceListItem): PieceRowValues {
  return {
    kind: piece.kind,
    label: piece.label,
    lengthMm: String(piece.lengthMm),
    widthMm: String(piece.widthMm),
    materialId: piece.materialId ?? '',
    edgeId: piece.edgeId ?? '',
    edgeCountLength: String(piece.edgeCountLength),
    edgeCountWidth: String(piece.edgeCountWidth),
    doubled: piece.doubled,
    quantity: String(piece.quantity ?? 1),
    coefficient: piece.coefficient,
    lengthFormula: piece.lengthFormula ?? '',
    widthFormula: piece.widthFormula ?? '',
  };
}

function pieceRowFromInput(piece: CabinetPieceInput): PieceRowValues {
  return {
    kind: piece.kind,
    label: piece.label,
    lengthMm: String(piece.lengthMm),
    widthMm: String(piece.widthMm),
    materialId: piece.materialId ?? '',
    edgeId: piece.edgeId ?? '',
    edgeCountLength: String(piece.edgeCountLength ?? 0),
    edgeCountWidth: String(piece.edgeCountWidth ?? 0),
    doubled: piece.doubled ?? false,
    // Blueprints written before the column existed carry no count.
    quantity: String(piece.quantity ?? 1),
    coefficient: piece.coefficient ?? '2.5',
    lengthFormula: piece.lengthFormula ?? '',
    widthFormula: piece.widthFormula ?? '',
  };
}

/** Form rows → the API's piece payload. */
function piecePayloadFromRows(rows: PieceRowValues[]): CabinetPieceInput[] {
  return rows.map((row) => ({
    kind: row.kind,
    label: row.label,
    lengthMm: Number(row.lengthMm) || 0,
    widthMm: Number(row.widthMm) || 0,
    materialId: row.materialId || null,
    edgeId: row.edgeId || null,
    edgeCountLength: Number(row.edgeCountLength) || 0,
    edgeCountWidth: Number(row.edgeCountWidth) || 0,
    doubled: row.doubled,
    quantity: Math.max(1, Math.trunc(Number(row.quantity) || 1)),
    coefficient: row.coefficient || undefined,
    // Empty means the millimetres were typed; the server stores null and leaves
    // the value alone.
    lengthFormula: row.lengthFormula || null,
    widthFormula: row.widthFormula || null,
  }));
}

/**
 * The Custom tab's geometry, as the cut-list generator wants it.
 *
 * Every number is re-coerced on the way through. `ManualValues` types these as
 * `number` because the schema declares `z.coerce.number()`, but that is the
 * *resolver's* output and only ever reaches the `handleSubmit` callback. This is
 * fed from `getValues()`, which hands back the raw input values — so `widthMm`
 * is really the string "600" here. The same coercion is why `previewBody` reads
 * `Number(watched.widthMm)` rather than trusting the field type.
 */
function parametricSpecFromValues(values: ManualValues): ParametricCabinetSpec {
  const int = (value: number): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  };
  return {
    widthMm: int(values.widthMm),
    heightMm: int(values.heightMm),
    depthMm: int(values.depthMm),
    shelvesCount: int(values.politeCount),
    dividersCount: int(values.intermediarCount),
    carcassDoubled: values.carcassDoubled,
    shelvesDoubled: values.politeDoubled,
    dividersDoubled: values.intermediarDoubled,
    // Matches `previewBody` and `computeParametricCabinet`: a face is priced only
    // when its material is set.
    hasFront: Boolean(values.frontMaterialId),
    hasBack: Boolean(values.backMaterialId),
  };
}

/**
 * Which material each seeded row takes.
 *
 * C.E.F. (`carcassExteriorFront`) swaps only the *material* onto the carcass
 * rows — `calculateCabinet` still prices their banding at the carcass edge — and
 * falls back to the carcass material when no front is picked, exactly as
 * `computeParametricCabinet` does.
 */
function parametricPieceMaterials(values: ManualValues): ParametricPieceMaterials {
  return {
    carcassMaterialId:
      values.carcassExteriorFront && values.frontMaterialId
        ? values.frontMaterialId
        : values.materialId,
    carcassEdgeId: values.edgeId,
    carcassCoefficient:
      values.carcassExteriorFront && values.frontMaterialId
        ? values.frontCoefficient
        : values.carcassCoefficient,
    frontCoefficient: values.frontCoefficient,
    backCoefficient: values.backCoefficient,
    frontMaterialId: values.frontMaterialId,
    frontEdgeId: values.frontMaterialId ? values.frontEdgeId : '',
    backMaterialId: values.backMaterialId,
  };
}

/** The baseline a hydrated cut list is considered to be in step with. */
function parametricBaseline(values: ManualValues) {
  return { spec: parametricSpecFromValues(values), materials: parametricPieceMaterials(values) };
}

/**
 * `pieces` is passed in rather than built here because the six seeded rows need
 * translated labels, and `t` is only available inside the component.
 */
function manualDefaults(
  defaultRoomId?: string,
  defaultMaterialId?: string,
  pieces: PieceRowValues[] = [],
): ManualValues {
  const matId = defaultMaterialId ?? '';
  return {
    roomId: defaultRoomId ?? '',
    areaId: '',
    name: '',
    sku: '',
    widthMm: 600,
    heightMm: 720,
    depthMm: 560,
    intermediarCount: 0,
    politeCount: 0,
    intermediarDoubled: false,
    carcassDoubled: false,
    carcassExteriorFront: false,
    politeDoubled: false,
    quantity: '1',
    coefficient: '2.5',
    carcassCoefficient: '2.5',
    frontCoefficient: '2.5',
    backCoefficient: '2.5',
    unitPriceNet: '0.00',
    notes: '',
    pricingMode: 'AUTO',
    materialId: matId,
    edgeId: '',
    frontMaterialId: '',
    frontEdgeId: '',
    backMaterialId: '',
    hardwareCatalogId: '',
    hardwareQuantity: '1',
    hardwareItems: [],
    joinTypeKind: 'EUROSCREW',
    constructionMode: 'PARAMETRIC',
    pieces,
    piecesBulkMaterialId: '',
    piecesBulkEdgeId: '',
  };
}

function catalogDefaults(defaultRoomId?: string): FromCatalogValues {
  return {
    catalogItemId: '',
    roomId: defaultRoomId ?? '',
    areaId: '',
    unitPriceNet: '0.00',
    quantity: '1',
    coefficient: '2.5',
    notes: '',
  };
}

function manualValuesFromCabinet(
  cabinet: CabinetListItem | CabinetDetails,
  defaultRoomId?: string,
): ManualValues {
  return {
    roomId: cabinet.roomId ?? defaultRoomId ?? '',
    areaId: cabinet.areaId ?? '',
    name: cabinet.name,
    // A cabinet built from a catalog blueprint snapshots the blueprint SKU
    // verbatim, and catalog SKUs allow 80 characters while this form — and both
    // create/updateCabinetRequest — cap them at 50. Since no input renders the
    // field, an over-cap value would pin a resolver error on a control nobody
    // can see: handleSubmit refuses, Save stays enabled, and nothing on screen
    // explains it — the dialog is simply dead, on copy and on edit alike.
    // So it only enters form state when the schema accepts it, never truncated
    // (a shortened SKU is a different SKU) — see `skuForCopy`.
    sku: skuForCopy(cabinet.sku, manualObjectSchema.shape.sku) ?? '',
    widthMm: cabinet.widthMm,
    heightMm: cabinet.heightMm,
    depthMm: cabinet.depthMm,
    intermediarCount: Math.max(0, cabinet.intermediarCount ?? 0),
    politeCount: Math.max(0, cabinet.politeCount ?? 0),
    intermediarDoubled: cabinet.intermediarDoubled,
    carcassDoubled: cabinet.carcassDoubled,
    carcassExteriorFront: cabinet.carcassExteriorFront,
    politeDoubled: cabinet.politeDoubled,
    quantity: positiveIntegerFromDecimal(cabinet.quantity) ?? '1',
    coefficient: cabinet.coefficient,
    carcassCoefficient: cabinet.carcassCoefficient,
    frontCoefficient: cabinet.frontCoefficient,
    backCoefficient: cabinet.backCoefficient,
    unitPriceNet: cabinet.unitPriceNet ?? '0.00',
    notes: cabinet.notes ?? '',
    pricingMode: 'AUTO',
    materialId: cabinet.materialId ?? '',
    edgeId: cabinet.edgeId ?? '',
    frontMaterialId: cabinet.frontMaterialId ?? '',
    frontEdgeId: cabinet.frontMaterialId ? (cabinet.frontEdgeId ?? '') : '',
    backMaterialId: cabinet.backMaterialId ?? '',
    hardwareCatalogId: '',
    hardwareQuantity: '1',
    hardwareItems: dedupeHardwareItems(
      'hardware' in cabinet
        ? cabinet.hardware
            .filter((item) => Boolean(item.hardwareCatalogId))
            .map((item) => ({
              hardwareCatalogId: item.hardwareCatalogId!,
              quantity: positiveIntegerFromDecimal(item.quantity) ?? '1',
              lengthM: item.lengthM ?? '0',
              coefficient: item.coefficient,
              name: item.name,
              sku: item.sku,
              manufacturer: item.material,
              pricePerPiece: item.unitPriceNet ?? '0',
              currency: item.currency,
            }))
        : [],
    ),
    joinTypeKind: cabinet.joinTypeKind ?? 'EUROSCREW',
    constructionMode: cabinet.constructionMode ?? 'PARAMETRIC',
    // Guard mirrors the `'hardware' in cabinet` check above — CabinetListItem
    // (the summary shape) carries no pieces, only CabinetDetails does.
    pieces: 'pieces' in cabinet ? cabinet.pieces.map(pieceRowFromListItem) : [],
    piecesBulkMaterialId: '',
    piecesBulkEdgeId: '',
  };
}

function manualValuesFromCatalogCabinet(
  item: CatalogItemDetails,
  defaultRoomId?: string,
  roomId?: string,
  areaId?: string,
): ManualValues {
  const parsed = catalogCabinetAttributes.safeParse(item.attributes ?? {});
  const attrs = parsed.success ? parsed.data : {};
  return {
    roomId: roomId ?? defaultRoomId ?? '',
    areaId: areaId ?? '',
    name: item.name,
    // Same 80-vs-50 gap as manualValuesFromCabinet, reached straight from the
    // Catalog tab: an 80-char blueprint SKU would deadlock this dialog too.
    // Dropped here, the server still snapshots `catalog.sku` itself.
    sku: skuForCopy(item.sku, manualObjectSchema.shape.sku) ?? '',
    widthMm: item.defaultWidthMm ?? 600,
    heightMm: item.defaultHeightMm ?? 720,
    depthMm: item.defaultDepthMm ?? 560,
    intermediarCount: Math.max(0, attrs.intermediarCount ?? 0),
    politeCount: Math.max(0, attrs.politeCount ?? 0),
    intermediarDoubled: attrs.intermediarDoubled ?? false,
    carcassDoubled: attrs.carcassDoubled ?? false,
    carcassExteriorFront: attrs.carcassExteriorFront ?? false,
    politeDoubled: attrs.politeDoubled ?? false,
    quantity: positiveIntegerFromDecimal(attrs.quantity) ?? '1',
    coefficient: attrs.coefficient ?? '2.5',
    carcassCoefficient: attrs.carcassCoefficient ?? attrs.coefficient ?? '2.5',
    frontCoefficient: attrs.frontCoefficient ?? attrs.coefficient ?? '2.5',
    backCoefficient: attrs.backCoefficient ?? attrs.coefficient ?? '2.5',
    unitPriceNet: item.defaultCostNet ?? '0.00',
    notes: item.description ?? '',
    pricingMode: 'AUTO',
    materialId: attrs.materialId ?? '',
    edgeId: attrs.edgeId ?? '',
    frontMaterialId: attrs.frontMaterialId ?? '',
    frontEdgeId: attrs.frontMaterialId ? (attrs.frontEdgeId ?? '') : '',
    backMaterialId: attrs.backMaterialId ?? '',
    hardwareCatalogId: '',
    hardwareQuantity: '1',
    hardwareItems: dedupeHardwareItems(
      (attrs.hardwareItems ?? []).map((hardware) => ({
        hardwareCatalogId: hardware.hardwareCatalogId,
        quantity: positiveIntegerFromDecimal(hardware.quantity) ?? '1',
        lengthM: '0',
        coefficient: hardware.coefficient,
        name: hardware.name,
        sku: hardware.sku,
        manufacturer: hardware.manufacturer,
        pricePerPiece: hardware.pricePerPiece,
        currency: hardware.currency,
      })),
    ),
    joinTypeKind: attrs.joinTypeKind ?? 'EUROSCREW',
    constructionMode: attrs.constructionMode ?? 'PARAMETRIC',
    pieces: (attrs.pieces ?? []).map(pieceRowFromInput),
    piecesBulkMaterialId: '',
    piecesBulkEdgeId: '',
  };
}

function buildCabinetCatalogBody(
  values: ManualValues,
  drawers: CreateDrawerRequest[],
  unitPriceNet: string,
  options?: { omitSku?: boolean },
): CreateCatalogItemRequest {
  const hardwareItems: CatalogCabinetAttributes['hardwareItems'] = dedupeHardwareItems(
    values.hardwareItems,
  ).map((item) => {
    const currency: 'RON' | 'EUR' | 'USD' | undefined =
      item.currency === 'RON' || item.currency === 'EUR' || item.currency === 'USD'
        ? item.currency
        : undefined;
    return {
      hardwareCatalogId: item.hardwareCatalogId,
      quantity: item.quantity,
      coefficient: item.coefficient,
      name: item.name,
      sku: item.sku,
      manufacturer: item.manufacturer,
      pricePerPiece: item.pricePerPiece,
      currency,
    };
  });
  const joinTypeLabels: Record<NonNullable<ManualValues['joinTypeKind']>, string> = {
    EUROSCREW: 'Eurosurub',
    DEMONTABIL: 'Demontabil',
    CLAMEX: 'Clamex',
    MITER_45: '45°',
    NONE: 'None',
  };
  const isPieces = values.constructionMode === 'PIECES';
  // Carried on a Custom blueprint too, so promoting one and picking it back up
  // returns the cabinet whole rather than just its parametric half.
  const pieces =
    isPieces || cutListHasUserInput(values.pieces ?? [])
      ? piecePayloadFromRows(values.pieces)
      : undefined;
  const derivedDims = isPieces && pieces ? deriveCabinetDimensionsFromPieces(pieces) : null;
  const attributes: CatalogCabinetAttributes = {
    materialId: values.materialId || null,
    edgeId: values.edgeId || null,
    frontMaterialId: values.frontMaterialId || null,
    frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
    backMaterialId: values.backMaterialId || null,
    backEdgeId: null,
    backHasEdge: false,
    intermediarCount: values.intermediarCount,
    politeCount: values.politeCount,
    joinType: joinTypeLabels[values.joinTypeKind],
    intermediarDoubled: values.intermediarDoubled,
    carcassDoubled: values.carcassDoubled,
    carcassExteriorFront: values.carcassExteriorFront,
    politeDoubled: values.politeDoubled,
    pricingMode: 'AUTO',
    joinTypeKind: values.joinTypeKind,
    hardwareItems,
    drawers,
    constructionMode: values.constructionMode,
    pieces,
  };
  return {
    type: 'CABINET',
    name: values.name,
    sku: options?.omitSku ? undefined : values.sku || undefined,
    description: values.notes || undefined,
    defaultWidthMm: derivedDims?.widthMm ?? values.widthMm,
    defaultHeightMm: derivedDims?.heightMm ?? values.heightMm,
    defaultDepthMm: derivedDims?.depthMm ?? values.depthMm,
    defaultCostNet: unitPriceNet,
    currency: 'RON',
    attributes: attributes as Record<string, unknown>,
  };
}

function drawerBodyFromListItem(drawer: DrawerListItem): CreateDrawerRequest {
  return {
    name: drawer.name ?? undefined,
    // Without the mode a copied drawer would freeze the source's positional
    // name — a copy of "Sus" staying "Sus" wherever it lands, and never
    // renumbering again.
    nameMode: drawer.nameMode,
    heightMm: drawer.heightMm ?? undefined,
    widthMm: drawer.widthMm ?? undefined,
    depthMm: drawer.depthMm ?? undefined,
    carcassMaterial: drawer.carcassMaterial ?? undefined,
    frontMaterial: drawer.frontMaterial ?? undefined,
    drawerModel: drawer.drawerModel ?? undefined,
    drawerType: drawer.drawerType ?? undefined,
    slideModel: drawer.slideModel ?? undefined,
    hBox: drawer.hBox ?? undefined,
    color: drawer.color ?? undefined,
    quantity: positiveIntegerFromDecimal(drawer.quantity) ?? '1',
    coefficient: drawer.coefficient,
    materialCoefficient: drawer.materialCoefficient,
    frontMaterialCoefficient: drawer.frontMaterialCoefficient,
    unitPriceNet: drawer.unitPriceNet ?? '0.00',
    currency: drawer.currency ?? 'RON',
    notes: drawer.notes ?? undefined,
    pricingMode: 'AUTO',
    materialId: drawer.materialId,
    edgeId: drawer.edgeId,
    frontMaterialId: drawer.frontMaterialId,
    frontEdgeId: drawer.frontEdgeId,
    drawerSystemVariantId: drawer.drawerSystemVariantId,
    hbox: drawer.hbox,
    drawerModelKind: drawer.drawerModelKind,
    joinType: drawer.joinType,
    cx2: drawer.cx2,
  };
}

function sameCatalogName(value: string, sourceName: string | undefined): boolean {
  if (!sourceName) return false;
  return value.trim().toLocaleLowerCase() === sourceName.trim().toLocaleLowerCase();
}

function drawerDraftId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseDecimalValue(value: string | number | null | undefined): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value ?? '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

function positiveIntegerFromDecimal(value: string | undefined): string | null {
  if (!value) return null;
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return String(Math.max(1, Math.trunc(parsed)));
}

function formatCurrencyValue(value: number): string {
  return `${value.toFixed(2)} RON`;
}

function drawerLineTotalNet(body: CreateDrawerRequest | DrawerListItem): number {
  if ('totalNet' in body && body.totalNet) return parseDecimalValue(body.totalNet);
  return (
    parseDecimalValue(body.unitPriceNet) *
    parseDecimalValue(body.quantity || '1') *
    parseDecimalValue(body.coefficient || '1')
  );
}

/**
 * Decide whether the edit form should be (re)hydrated from the server copy.
 *
 * The cabinet detail query is invalidated by sibling mutations — notably any
 * drawer (Sertar) create/update/delete — so it can refetch *while the user is
 * mid-edit*. Blindly resetting on every refetch discards unsaved Feronerie and
 * field changes before the user hits Save. We therefore hydrate only when:
 *   - it's the first hydration (`lastHydratedKey === null`),
 *   - a genuinely different cabinet is shown (id changed), or
 *   - the user has no unsaved changes.
 *
 * `key` shape is `${cabinetId}:${updatedAt}:${sourceKey}`; the cabinet id is a
 * cuid (no colons), so `split(':')[0]` recovers it.
 */
export function shouldHydrateCabinetEditForm(params: {
  lastHydratedKey: string | null;
  nextKey: string;
  nextCabinetId: string;
  isDirty: boolean;
}): boolean {
  const { lastHydratedKey, nextKey, nextCabinetId, isDirty } = params;
  if (lastHydratedKey === nextKey) return false;
  if (lastHydratedKey === null) return true;
  if (lastHydratedKey.split(':')[0] !== nextCabinetId) return true;
  return !isDirty;
}

export function AddCabinetDialog({
  projectId,
  defaultRoomId,
  cabinet,
  mode = 'edit',
  open,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const defaultMaterialId = useDefaultMaterialId();
  const isCopy = Boolean(cabinet) && mode === 'copy';
  const isEdit = Boolean(cabinet) && !isCopy;
  // The source row in both modes: a copy reads its pieces, feronerie and
  // sertare from here, it just never saves back to it.
  const editCabinetId = cabinet?.id ?? '';
  const [tab, setTab] = useState<CabinetTab>('manual');
  const [sourceCatalogItemId, setSourceCatalogItemId] = useState<string | null>(null);
  const [hardwareCandidate, setHardwareCandidate] = useState<CatalogHardwareListItem | null>(null);
  const [hardwarePickerKey, setHardwarePickerKey] = useState(0);
  const [showAllHardware, setShowAllHardware] = useState(false);
  const [showAllDrawers, setShowAllDrawers] = useState(false);
  const [drawerDrafts, setDrawerDrafts] = useState<DrawerDraft[]>([]);
  const [drawerDraftsDirty, setDrawerDraftsDirty] = useState(false);
  const [drawerDraftDialogOpen, setDrawerDraftDialogOpen] = useState(false);
  const [editingDrawerDraft, setEditingDrawerDraft] = useState<DrawerDraft | null>(null);
  const [showAddExistingDrawer, setShowAddExistingDrawer] = useState(false);
  const [editingExistingDrawer, setEditingExistingDrawer] = useState<DrawerListItem | null>(null);
  const [pendingExistingDrawerDelete, setPendingExistingDrawerDelete] =
    useState<DrawerListItem | null>(null);
  const [selectedCarcassMaterial, setSelectedCarcassMaterial] = useState<MaterialListItem | null>(
    null,
  );
  const [selectedFrontMaterial, setSelectedFrontMaterial] = useState<MaterialListItem | null>(null);
  const coefficientPrompt = useCatalogCoefficientPrompt();

  const createMut = useCreateCabinet(projectId);
  const fromCatalogMut = useCreateCabinetFromCatalog(projectId);
  const updateMut = useUpdateCabinet(projectId, editCabinetId);
  const saveCatalogBlueprint = useSaveCatalogBlueprint();
  const editCabinetQuery = useCabinet(projectId, editCabinetId, open && (isEdit || isCopy));
  const existingDrawers = useDrawers(projectId, editCabinetId, open && (isEdit || isCopy));
  const deleteDrawerMut = useDeleteDrawer(projectId, editCabinetId);
  const editCabinet = editCabinetQuery.data ?? cabinet ?? null;
  const editCabinetSourceKey = editCabinetQuery.data
    ? `details:${editCabinetQuery.data.hardware.length}:${editCabinetQuery.data.drawers.length}`
    : 'summary';
  const editCabinetResetKey = editCabinet
    ? `${editCabinet.id}:${editCabinet.updatedAt}:${editCabinetSourceKey}`
    : '';
  // Where the values on screen came from. A copy is seeded from the source row
  // exactly like an edit is, so its catalog link counts here as well — without
  // it a copy of a catalog-derived row would re-send the blueprint's globally
  // unique SKU and be rejected as CATALOG_SKU_TAKEN.
  const sourceCatalogItemIdForSave =
    sourceCatalogItemId ?? (isEdit || isCopy ? (editCabinet?.catalogItemId ?? null) : null);
  const sourceCatalogItemForSave = useCatalogItem(sourceCatalogItemIdForSave || undefined);
  // Tracks the last server snapshot the edit form was hydrated from, so a
  // background refetch (e.g. triggered by a drawer mutation invalidating the
  // cabinet detail query) does not clobber unsaved edits. See
  // shouldHydrateCabinetEditForm.
  const lastHydratedKeyRef = useRef<string | null>(null);

  const roomsQuery = useRooms(projectId);
  const roomOptions = useMemo(
    () => (roomsQuery.data ?? []).map((r) => ({ value: r.id, label: r.name })),
    [roomsQuery.data],
  );

  const pieceKindLabel = (kind: CabinetPieceKindValue) =>
    t(`projectItems.cabinets.piecesTable.kinds.${kind}`);
  /**
   * The Custom values the cut list on screen was generated from.
   *
   * This is what makes "you changed the Custom fields — apply to the pieces?"
   * answerable: without a baseline the dialog cannot tell an edit the rows
   * should follow from one the user already reconciled by hand. Re-seeded on
   * every hydration (open / edit / catalog), so a saved cabinet opens settled
   * rather than immediately claiming to be out of date.
   */
  const [appliedParametric, setAppliedParametric] = useState<{
    spec: ParametricCabinetSpec;
    materials: ParametricPieceMaterials;
  } | null>(null);

  const priceForm = useForm<ManualPriceValues>({
    resolver: zodResolver(manualPriceSchema),
    defaultValues:
      cabinet && isManualPriceCabinet(cabinet)
        ? manualPriceValuesFromCabinet(cabinet, defaultRoomId)
        : manualPriceDefaults(defaultRoomId),
  });
  const manualForm = useForm<ManualValues>({
    resolver: zodResolver(manualSchema),
    defaultValues: cabinet
      ? manualValuesFromCabinet(cabinet, defaultRoomId)
      : manualDefaults(defaultRoomId, undefined, defaultPieceRows(pieceKindLabel)),
  });
  const hardwareFieldArray = useFieldArray({
    control: manualForm.control,
    name: 'hardwareItems',
  });
  const pieceFieldArray = useFieldArray({
    control: manualForm.control,
    name: 'pieces',
  });
  const watchedRaw = manualForm.watch();

  /**
   * The board behind each of the three markups below the pickers, so leaving
   * one can ask whether the number becomes that board's catalog default.
   *
   * Resolved from the id on the form rather than from the picker's callback:
   * a cabinet opened for editing never fires one, and editing is where markups
   * get retyped. The cut list is deliberately left out — its rows carry a
   * markup each and share boards freely, so one material used on twenty rows
   * would ask the same question twenty times.
   */
  const carcassCoefficientTarget = useCoefficientTarget(
    'material',
    watchedRaw.materialId,
    t('projectItems.cabinets.fields.materialId'),
  );
  const frontCoefficientTarget = useCoefficientTarget(
    'material',
    watchedRaw.frontMaterialId,
    t('projectItems.cabinets.fields.frontMaterialId'),
  );
  const backCoefficientTarget = useCoefficientTarget(
    'material',
    watchedRaw.backMaterialId,
    t('projectItems.cabinets.fields.backMaterialId'),
  );

  /** Add a cut-list row in its kind's slot, inheriting the bulk pickers. */
  const insertPieceRow = (kind: CabinetPieceKindValue, doubled: boolean) => {
    const rows = manualForm.getValues('pieces') ?? [];
    const values = manualForm.getValues();
    const spec = parametricSpecFromValues(values);
    const materials = parametricPieceMaterials(values);
    // A new side starts from what the cabinet already is — the two dimensions
    // its kind spans, and the material and edge already in play — so the common
    // case needs no typing at all. Copied, not linked: these stay put when the
    // Custom fields move, which is what a formula is for.
    const axes = cabinetPieceAxes(kind);
    const fromAxis = (axis: ReturnType<typeof cabinetPieceAxes>['lengthAxis']) =>
      axis === 'HEIGHT'
        ? spec.heightMm
        : axis === 'WIDTH'
          ? spec.widthMm
          : axis === 'DEPTH'
            ? spec.depthMm
            : 0;
    // Slot the row under its own kind instead of appending, so the cut list
    // stays grouped (Latura stânga 2 under Latura stânga 1).
    pieceFieldArray.insert(
      cabinetPieceInsertIndex(
        rows.map((row) => row.kind),
        kind,
      ),
      {
        ...makePieceRow(kind, pieceKindLabel(kind), nextPieceNumber(rows, kind), {
          // A row added after "apply to all" inherits that choice — otherwise it
          // would silently arrive on a different board.
          materialId:
            manualForm.getValues('piecesBulkMaterialId') ||
            materials.carcassMaterialId ||
            defaultMaterialId,
          edgeId: manualForm.getValues('piecesBulkEdgeId') || materials.carcassEdgeId || undefined,
          doubled,
        }),
        // Only when the cabinet actually has them: a blank Custom form has
        // nothing worth copying, and a 0 mm row is worse than an empty one.
        lengthMm: fromAxis(axes.lengthAxis) > 0 ? String(fromAxis(axes.lengthAxis)) : '',
        widthMm: fromAxis(axes.widthAxis) > 0 ? String(fromAxis(axes.widthAxis)) : '',
      },
    );
  };

  /**
   * Copy a cut-list row in directly under itself.
   *
   * Unlike "Adaugă <kind>" this inherits nothing from the Custom fields or the
   * bulk pickers — the point of duplicating is that the row beside it is
   * already right. Slotting it at `index + 1` keeps the kind grouped without
   * needing `cabinetPieceInsertIndex`, since the source row is already in its
   * own group.
   */
  const duplicatePieceRow = (index: number) => {
    const copy = duplicatedPieceRow(manualForm.getValues('pieces') ?? [], index);
    if (!copy) return;
    pieceFieldArray.insert(index + 1, copy);
  };

  /**
   * Replace the cut list with a freshly generated one and move to that tab.
   *
   * Split out of `requestSwitchToPieces` so the rows are built once — at the
   * click — and applied unchanged whether or not the overwrite prompt intervened.
   */
  const applySwitchToPieces = (
    rows: PieceRowValues[],
    materials: ParametricPieceMaterials,
    spec: ParametricCabinetSpec,
  ) => {
    setAppliedParametric({ spec, materials });
    pieceFieldArray.replace(rows);
    // Drop any resolver errors still pinned to the old rows.
    //
    // `replace` calls RHF's `_setFieldArray` with shouldUpdateFieldsAndState
    // false, which makes it skip remapping `errors.pieces` entirely, and the
    // `trigger` above only touched the four names it was given. So a user who
    // hit Save once on the blank cut list would land back here with the rows
    // correctly filled in and every one of them still flagged "Must be greater
    // than 0" — cleared only by editing each field by hand. Runs after the
    // replace, so the array's own state push cannot re-emit the stale object.
    manualForm.clearErrors('pieces');
    // Arm the table's "apply to all" bar with what the carcass rows just took, so
    // a row added afterwards inherits it — insertPieceRow reads exactly these two.
    manualForm.setValue('piecesBulkMaterialId', materials.carcassMaterialId, {
      shouldDirty: true,
    });
    manualForm.setValue('piecesBulkEdgeId', materials.carcassEdgeId, { shouldDirty: true });
    // The mode lives in form state, not just the tab: it decides which branch of
    // the schema validates and what the submit body sends. Same pair of steps the
    // tab strip performs.
    manualForm.setValue('constructionMode', 'PIECES', { shouldDirty: true });
    setTab('pieces');
  };

  /**
   * "Treci pe piese" — turn what is on the Custom tab into an editable cut list.
   *
   * The generated rows reproduce the parametric calculator's own terms, so the
   * cabinet costs the same before and after; see `cabinetPiecesFromParametric`
   * for the mapping and the two documented divergences (glass-sanded carcass
   * boards, and where BOM attributes C.E.F. square metres).
   */
  const requestSwitchToPieces = async () => {
    // Validate first: without this a blank or zero H/W/D would seed 0 mm rows,
    // which the cut list cannot flag and the pieces preview silently drops.
    const valid = await manualForm.trigger(['widthMm', 'heightMm', 'depthMm', 'materialId']);
    if (!valid) return;

    const values = manualForm.getValues();
    const spec = parametricSpecFromValues(values);
    // No size pre-flight: shelves and dividers collapse into one counted row
    // each, so the cut list is at most MAX_SEEDED_CABINET_PIECES rows however
    // many the Custom form asks for.
    const materials = parametricPieceMaterials(values);

    // A cut list the user has actually worked on is never overwritten by the
    // switch itself. Moving to it just shows it; if the Custom fields have since
    // moved on, the bar above the table offers to apply them.
    if (cutListHasUserInput(values.pieces ?? [])) {
      manualForm.setValue('constructionMode', 'PIECES', { shouldDirty: true });
      setTab('pieces');
      return;
    }
    applySwitchToPieces(pieceRowsFromParametric(spec, materials, pieceKindLabel), materials, spec);
  };

  /**
   * Back to the Custom form. The cut list stays in form state — switching is a
   * change of how the cabinet is *described* and priced, not a discard — so
   * returning to the pieces restores exactly the rows that were there.
   */
  const switchToCustom = () => {
    manualForm.setValue('constructionMode', 'PARAMETRIC', { shouldDirty: true });
    setTab('manual');
  };

  /**
   * Rewrite the rows the Custom fields still own, leaving hand-edited ones be.
   *
   * A row counts as "still owned" when it is byte-for-byte what the previous
   * Custom values generated: anything the user has since retyped, re-materialled
   * or renamed no longer matches and is kept. Rows are never deleted here — a
   * front the user dropped from Custom leaves its FRONT row in place rather than
   * silently vanishing from the cut list.
   */
  const applyParametricToPieces = () => {
    const values = manualForm.getValues();
    const spec = parametricSpecFromValues(values);
    const materials = parametricPieceMaterials(values);
    const nextRows = pieceRowsFromParametric(spec, materials, pieceKindLabel);
    const previousRows = appliedParametric
      ? pieceRowsFromParametric(appliedParametric.spec, appliedParametric.materials, pieceKindLabel)
      : [];

    const current = [...(values.pieces ?? [])];
    for (const next of nextRows) {
      const previous = previousRows.find((row) => row.kind === next.kind);
      const at = current.findIndex((row) => row.kind === next.kind);
      if (at === -1) {
        current.splice(
          cabinetPieceInsertIndex(
            current.map((row) => row.kind),
            next.kind,
          ),
          0,
          next,
        );
        continue;
      }
      const untouched = previous && JSON.stringify(current[at]) === JSON.stringify(previous);
      if (untouched) current[at] = next;
    }
    applySwitchToPieces(current, materials, spec);
  };

  // Read `isDirty` during render so RHF's formState proxy subscribes to it and
  // keeps it current — the hydration guard below depends on an accurate value.
  // Either form can hold the unsaved edit a refetch must not discard — a
  // lump-sum row is edited on priceForm, which manualForm knows nothing about.
  const isFormDirty = manualForm.formState.isDirty || priceForm.formState.isDirty;
  // Carcass and front each resolve their edge from the picked material's
  // manufacturer. Same hook the cut-list rows use, so all three stay in step.
  useAutoSelectedEdge({
    material: selectedCarcassMaterial,
    enabled: open,
    preferLargestProduct: watchedRaw.carcassDoubled,
    onResolved: (edgeId) => {
      manualForm.setValue('edgeId', edgeId, { shouldDirty: true, shouldValidate: true });
      manualForm.clearErrors('edgeId');
    },
  });
  useAutoSelectedEdge({
    material: selectedFrontMaterial,
    enabled: open,
    onResolved: (edgeId) => {
      manualForm.setValue('frontEdgeId', edgeId, { shouldDirty: true, shouldValidate: true });
      manualForm.clearErrors('frontEdgeId');
    },
  });

  // Which side of the cabinet the dialog is showing. An edit opens on whichever
  // one it was saved in — the hydration effect below sets `tab` from the stored
  // construction mode — and the switch beside the name moves between them from
  // then on, exactly as when adding.
  const showPieces = tab === 'pieces';

  // ---- Live AUTO preview ---------------------------------------------------
  const watched = useDebouncedValue(watchedRaw, 300);
  const previewBody: PreviewCabinetRequest | null = useMemo(() => {
    if (showPieces) return null;
    const widthMm = Number(watched.widthMm);
    const heightMm = Number(watched.heightMm);
    const depthMm = Number(watched.depthMm);
    const shelvesCount = Number(watched.politeCount ?? 0);
    const dividersCount = Number(watched.intermediarCount ?? 0);
    if (
      !watched.materialId ||
      !watched.joinTypeKind ||
      !Number.isFinite(widthMm) ||
      widthMm <= 0 ||
      !Number.isFinite(heightMm) ||
      heightMm <= 0 ||
      !Number.isFinite(depthMm) ||
      depthMm <= 0 ||
      !Number.isFinite(shelvesCount) ||
      shelvesCount < 0 ||
      !Number.isFinite(dividersCount) ||
      dividersCount < 0
    ) {
      return null;
    }
    return {
      widthMm,
      heightMm,
      depthMm,
      carcassMaterialId: watched.materialId,
      carcassEdgeId: watched.edgeId || null,
      frontMaterialId: watched.frontMaterialId || null,
      frontEdgeId: watched.frontMaterialId ? watched.frontEdgeId || null : null,
      backMaterialId: watched.backMaterialId || null,
      backEdgeId: null,
      backHasEdge: false,
      carcassDoubled: watched.carcassDoubled ?? false,
      carcassExteriorFront: watched.carcassExteriorFront ?? false,
      shelvesCount,
      shelvesDoubled: watched.politeDoubled ?? false,
      dividersCount,
      dividersDoubled: watched.intermediarDoubled ?? false,
      hardware: (watched.hardwareItems ?? [])
        .filter((item) => item.name && item.pricePerPiece !== undefined)
        .map((item) => ({
          name: item.name ?? '',
          quantity: item.quantity || '1',
          lengthM: item.lengthM || '0',
          coefficient: item.coefficient || '1.00',
          unitPriceNet: item.pricePerPiece ?? '0',
        })),
      joinType: watched.joinTypeKind,
      quantity: watched.quantity || '1',
      carcassCoefficient: watched.carcassCoefficient || '2.5',
      frontCoefficient: watched.frontCoefficient || '2.5',
      backCoefficient: watched.backCoefficient || '2.5',
    };
  }, [watched, showPieces]);

  const piecesPreviewBody: PreviewCabinetPiecesRequest | null = useMemo(() => {
    if (!showPieces) return null;
    // Half-filled rows cannot be priced; drop them rather than 400 the request.
    const rows = (watched.pieces ?? []).filter(
      (piece) => piece.materialId && Number(piece.lengthMm) > 0 && Number(piece.widthMm) > 0,
    );
    if (rows.length === 0) return null;
    return {
      pieces: rows.map((piece) => ({
        label: piece.label,
        kind: piece.kind,
        lengthMm: Number(piece.lengthMm),
        widthMm: Number(piece.widthMm),
        materialId: piece.materialId,
        edgeId: piece.edgeId || null,
        edgeCountLength: Number(piece.edgeCountLength) || 0,
        edgeCountWidth: Number(piece.edgeCountWidth) || 0,
        doubled: piece.doubled,
        quantity: Math.max(1, Math.trunc(Number(piece.quantity) || 1)),
        coefficient: piece.coefficient || '2.5',
      })),
      hardware: (watched.hardwareItems ?? [])
        .filter((item) => item.name && item.pricePerPiece !== undefined)
        .map((item) => ({
          name: item.name ?? '',
          quantity: item.quantity || '1',
          lengthM: item.lengthM || '0',
          coefficient: item.coefficient || '1.00',
          unitPriceNet: item.pricePerPiece ?? '0',
        })),
      quantity: watched.quantity || '1',
    };
  }, [watched, showPieces]);

  /**
   * Whether the Custom fields have moved on since the cut list was generated.
   *
   * Read off the debounced values, so typing "800" does not flash the bar at
   * "8" and "80". A null baseline means no cut list has been generated or
   * hydrated yet, and there is nothing to be out of date with.
   */
  const parametricDrifted = useMemo(() => {
    if (!showPieces || !appliedParametric) return false;
    return (
      JSON.stringify({
        spec: parametricSpecFromValues(watched),
        materials: parametricPieceMaterials(watched),
      }) !== JSON.stringify(appliedParametric)
    );
  }, [appliedParametric, showPieces, watched]);

  // Both hooks must be called unconditionally (rules of hooks); the inactive one
  // is idled with a null body. Everything downstream reads `preview`.
  const parametricPreview = usePreviewCabinet(previewBody);
  const piecesPreview = usePreviewCabinetPieces(piecesPreviewBody);
  const preview = showPieces ? piecesPreview : parametricPreview;
  const activePreviewBody = showPieces ? piecesPreviewBody : previewBody;
  useEffect(() => {
    if (preview.data && activePreviewBody) {
      const subtotal = parseFloat(preview.data.breakdown.subtotalNet);
      if (Number.isFinite(subtotal) && subtotal >= 0) {
        manualForm.setValue('unitPriceNet', subtotal.toFixed(2), { shouldDirty: false });
      }
    }
  }, [preview.data, activePreviewBody, manualForm]);
  // Saved drawers already carry the name the server resolved, generated or not.
  // Drafts have not been named yet, so they are previewed from their position.
  const draftNames = useMemo(() => drawerDraftDisplayNames(drawerDrafts), [drawerDrafts]);
  const drawerPricingSummary = useMemo(() => {
    const rows = isEdit
      ? (existingDrawers.data ?? []).map((drawer) => ({
          id: drawer.id,
          name: drawer.name || 'Sertar',
          totalNet: drawerLineTotalNet(drawer),
        }))
      : drawerDrafts.map((draft) => ({
          id: draft.id,
          name: draftNames.get(draft.id) ?? 'Sertar',
          totalNet: drawerLineTotalNet(draft.body),
        }));
    return {
      rows,
      totalNet: rows.reduce((sum, row) => sum + row.totalNet, 0),
    };
  }, [draftNames, drawerDrafts, existingDrawers.data, isEdit]);
  const cabinetTotalForCombined = preview.data?.breakdown
    ? parseDecimalValue(preview.data.breakdown.totalNet)
    : null;
  const combinedPreviewTotalNet =
    cabinetTotalForCombined !== null && drawerPricingSummary.totalNet > 0
      ? cabinetTotalForCombined + drawerPricingSummary.totalNet
      : null;

  const catalogForm = useForm<FromCatalogValues>({
    resolver: zodResolver(fromCatalogSchema),
    defaultValues: catalogDefaults(defaultRoomId),
  });
  // A lump-sum row must come back to the lump-sum form: the full form would show
  // it as a 0x0x0 AUTO cabinet and, on save, recompute its price away.
  const editsManualPrice =
    (isEdit || isCopy) && editCabinet !== null && isManualPriceCabinet(editCabinet);
  const selectedCatalogItemId = catalogForm.watch('catalogItemId');
  const selectedCatalogItemQuery = useCatalogItem(selectedCatalogItemId || undefined);

  useEffect(() => {
    const item = selectedCatalogItemQuery.data;
    if (!item || item.type !== 'CABINET' || sourceCatalogItemId === item.id) return;
    const parsed = catalogCabinetAttributes.safeParse(item.attributes ?? {});
    const attrs = parsed.success ? parsed.data : {};
    const fromCatalog = manualValuesFromCatalogCabinet(
      item,
      defaultRoomId,
      catalogForm.getValues('roomId'),
      catalogForm.getValues('areaId'),
    );
    manualForm.reset(fromCatalog);
    // A blueprint's rows were generated from its own attributes, so it arrives in
    // step — the bar must not greet the user on a cabinet they have not touched.
    setAppliedParametric(parametricBaseline(fromCatalog));
    setDrawerDrafts(
      (attrs.drawers ?? []).map((body) => ({
        id: drawerDraftId(),
        body,
      })),
    );
    setDrawerDraftsDirty(false);
    setSourceCatalogItemId(item.id);
    setHardwarePickerKey((key) => key + 1);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
    setShowAllHardware(false);
    setShowAllDrawers(false);
    // Land on whichever tab can actually edit the loaded blueprint.
    setTab(attrs.constructionMode === 'PIECES' ? 'pieces' : 'manual');
  }, [catalogForm, defaultRoomId, manualForm, selectedCatalogItemQuery.data, sourceCatalogItemId]);

  useEffect(() => {
    if (!open) {
      lastHydratedKeyRef.current = null;
      return;
    }
    if (isEdit || isCopy) {
      // The strip is hidden in edit mode; `showPieces` reads the stored mode.
      if (editsManualPrice) setTab('price');
      else setTab(editCabinet?.constructionMode === 'PIECES' ? 'pieces' : 'manual');
      // Only hydrate from the server copy when it's safe to do so — never
      // overwrite unsaved edits. A drawer (Sertar) mutation invalidates this
      // cabinet's detail query, so it can refetch mid-edit; without this guard
      // the refetch would silently discard in-progress Feronerie/field changes
      // before the user hits Save.
      if (
        editCabinet &&
        shouldHydrateCabinetEditForm({
          lastHydratedKey: lastHydratedKeyRef.current,
          nextKey: editCabinetResetKey,
          nextCabinetId: editCabinet.id,
          isDirty: isFormDirty,
        })
      ) {
        // The one field a copy does not take verbatim — the name is all that
        // tells the two rows apart in the table.
        const prefillName = isCopy ? copyName(editCabinet.name) : editCabinet.name;
        const hydrated = {
          ...manualValuesFromCabinet(editCabinet, defaultRoomId),
          name: prefillName,
        };
        manualForm.reset(hydrated);
        // Same reasoning as the catalog path: the stored rows and the stored
        // Custom fields describe one cabinet until the user edits one of them.
        setAppliedParametric(parametricBaseline(hydrated));
        // Hydrated under the same guard, so a mid-edit refetch cannot discard an
        // unsaved name or price.
        if (editsManualPrice) {
          priceForm.reset({
            ...manualPriceValuesFromCabinet(editCabinet, defaultRoomId),
            name: prefillName,
          });
        }
        lastHydratedKeyRef.current = editCabinetResetKey;
        setHardwarePickerKey((key) => key + 1);
        setSelectedCarcassMaterial(null);
        setSelectedFrontMaterial(null);
        setShowAllHardware(false);
        setShowAllDrawers(false);
      }
      return;
    }
    manualForm.reset(
      manualDefaults(
        defaultRoomId,
        defaultMaterialId,
        // Seed the six standard rows pre-filled with the default material so the
        // price preview fires as soon as dimensions are typed.
        defaultPieceRows(pieceKindLabel, defaultMaterialId),
      ),
    );
    setAppliedParametric(null);
    catalogForm.reset(catalogDefaults(defaultRoomId));
    priceForm.reset(manualPriceDefaults(defaultRoomId));
    setSourceCatalogItemId(null);
    setTab('manual');
    setDrawerDrafts([]);
    setDrawerDraftsDirty(false);
    setHardwareCandidate(null);
    setHardwarePickerKey((key) => key + 1);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
    setShowAllHardware(false);
    setShowAllDrawers(false);
  }, [catalogForm, defaultRoomId, editCabinetResetKey, isCopy, isEdit, manualForm, open]);

  // Fill default material once it resolves (async), only for blank new items —
  // a copy keeps whatever the source was built from.
  useEffect(() => {
    if (!open || isEdit || isCopy || !defaultMaterialId) return;
    const current = manualForm.getValues('materialId');
    if (!current) {
      manualForm.setValue('materialId', defaultMaterialId);
    }
  }, [defaultMaterialId, isCopy, isEdit, manualForm, open]);

  // The row a copy starts from is the list summary, which carries no cut list
  // and no feronerie — only the detail query does. Those two are therefore
  // topped up on their own: the guarded re-hydration above is skipped once the
  // form is dirty, and the name field is focused on open, so the user is very
  // likely typing in exactly the window where the details land.
  useEffect(() => {
    if (!open || !isCopy) return;
    const details = editCabinetQuery.data;
    if (!details) return;
    const fromDetails = manualValuesFromCabinet(details, defaultRoomId);
    hardwareFieldArray.replace(fromDetails.hardwareItems);
    pieceFieldArray.replace(fromDetails.pieces);
    // The field arrays are rebuilt on every render, so listing them here would
    // loop; the query snapshot is what decides when this runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRoomId, editCabinetQuery.data, isCopy, open]);

  // A copy has to bring the source's sertare with it, and the create path only
  // accepts them as drafts (the same ones a brand-new cabinet collects before
  // its first save). Seeded once the source list resolves, and never over the
  // top of drafts the user has already touched.
  useEffect(() => {
    if (!open || !isCopy || drawerDraftsDirty) return;
    const sourceDrawers = existingDrawers.data;
    if (!sourceDrawers) return;
    setDrawerDrafts(
      sourceDrawers.map((drawer) => ({
        id: drawerDraftId(),
        body: drawerBodyFromListItem(drawer),
      })),
    );
  }, [drawerDraftsDirty, existingDrawers.data, isCopy, open]);

  const handleClose = () => {
    if (
      createMut.isPending ||
      fromCatalogMut.isPending ||
      updateMut.isPending ||
      saveCatalogBlueprint.isPending
    ) {
      return;
    }
    manualForm.reset();
    catalogForm.reset();
    priceForm.reset();
    setSourceCatalogItemId(null);
    setTab('manual');
    setHardwareCandidate(null);
    setHardwarePickerKey((key) => key + 1);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
    setShowAllHardware(false);
    setShowAllDrawers(false);
    setDrawerDrafts([]);
    setDrawerDraftsDirty(false);
    setAppliedParametric(null);
    setDrawerDraftDialogOpen(false);
    setEditingDrawerDraft(null);
    setShowAddExistingDrawer(false);
    setEditingExistingDrawer(null);
    setPendingExistingDrawerDelete(null);
    onClose();
  };

  const addSelectedHardware = async () => {
    if (!hardwareCandidate) return;

    const currentItems = manualForm.getValues('hardwareItems') ?? [];
    const existingIndex = currentItems.findIndex(
      (item) => item.hardwareCatalogId === hardwareCandidate.id,
    );

    if (existingIndex >= 0) {
      if (existingIndex > 0) hardwareFieldArray.move(existingIndex, 0);
    } else {
      const quantityValid = await manualForm.trigger('hardwareQuantity');
      if (!quantityValid) return;
      const hardwareQuantity = manualForm.getValues('hardwareQuantity') || '1';
      hardwareFieldArray.prepend({
        hardwareCatalogId: hardwareCandidate.id,
        quantity: positiveIntegerString.safeParse(hardwareQuantity).success
          ? hardwareQuantity
          : '1',
        lengthM: '0',
        // The fitting brings its own markup, the way a board does. Typing over
        // it is what raises "make this the default?" on the way out of the
        // field.
        coefficient: hardwareCandidate.coefficient,
        name: hardwareCandidate.name,
        sku: hardwareCandidate.sku,
        manufacturer: hardwareCandidate.manufacturer,
        pricePerPiece: hardwareCandidate.pricePerPiece,
        currency: hardwareCandidate.currency,
      });
    }

    manualForm.setValue('hardwareCatalogId', '', {
      shouldDirty: false,
      shouldValidate: false,
    });
    manualForm.setValue('hardwareQuantity', '1', {
      shouldDirty: false,
      shouldValidate: false,
    });
    setHardwareCandidate(null);
    setHardwarePickerKey((key) => key + 1);
  };

  const replaceHardwareItem = (index: number, item: CatalogHardwareListItem) => {
    const next = hardwareItemsWithReplacement(
      manualForm.getValues('hardwareItems') ?? [],
      index,
      item,
    );
    if (next) hardwareFieldArray.replace(next);
  };

  const closeDrawerDraftDialog = () => {
    setDrawerDraftDialogOpen(false);
    setEditingDrawerDraft(null);
  };

  const saveDrawerDraft = (body: CreateDrawerRequest) => {
    setDrawerDraftsDirty(true);
    // Only on an add: editing a draft in place does not move it, so there is
    // nothing new to reveal.
    if (!editingDrawerDraft) setShowAllDrawers(true);
    setDrawerDrafts((current) => {
      // Appended, not prepended: a drawer stack reads top-down, so the first
      // drawer added is the top one ("Sus" / "Sertar 1") and each new one goes
      // beneath it. The array order is the orderIndex the server will assign.
      if (!editingDrawerDraft) return [...current, { id: drawerDraftId(), body }];
      return current.map((draft) =>
        draft.id === editingDrawerDraft.id ? { ...draft, body } : draft,
      );
    });
  };

  const submitManual = async (values: ManualValues) => {
    // Mirrors the disabled submit button: a form can still be submitted from the
    // keyboard, and an incomplete copy must never reach the create endpoint —
    // whether the source reads are still in flight or have failed outright.
    if (!copySourceReady) return;
    const joinTypeLabels: Record<NonNullable<ManualValues['joinTypeKind']>, string> = {
      EUROSCREW: 'Eurosurub',
      DEMONTABIL: 'Demontabil',
      CLAMEX: 'Clamex',
      MITER_45: '45°',
      NONE: 'None',
    };
    const hardwareItems = dedupeHardwareItems(values.hardwareItems).map((item) => ({
      hardwareCatalogId: item.hardwareCatalogId,
      quantity: item.quantity,
      lengthM: item.lengthM || '0',
      coefficient: item.coefficient,
    }));
    const isPieces = values.constructionMode === 'PIECES';
    // Sent whichever mode the cabinet is in: the rows are part of it, so going
    // back to Custom must not throw away work done on the pieces. Untouched
    // scaffolding is omitted — it would fail the API's `min(1)` dimensions and
    // describes nothing anyway.
    const piecePayload =
      isPieces || cutListHasUserInput(values.pieces ?? [])
        ? piecePayloadFromRows(values.pieces)
        : undefined;
    // A cut list extends the Custom cabinet rather than replacing it, so every
    // parametric field it was generated from is sent as-is. Only a PIECES
    // cabinet takes its gabarit from the rows — a Custom one keeps its own.
    const piecesGeometry =
      isPieces && piecePayload ? deriveCabinetDimensionsFromPieces(piecePayload) : {};
    const commonBody = {
      roomId: values.roomId || undefined,
      areaId: values.areaId || undefined,
      name: values.name,
      // Blank means "nothing to send": either the source had no SKU, or its
      // snapshot was too long for the schema and was kept out of form state
      // (see manualValuesFromCabinet). Omitting it leaves the stored SKU alone
      // on an update and the blueprint's own snapshot alone on a from-catalog
      // create; the plain create path simply saves the copy without one.
      sku: values.sku || undefined,
      widthMm: values.widthMm,
      heightMm: values.heightMm,
      depthMm: values.depthMm,
      intermediarCount: values.intermediarCount,
      politeCount: values.politeCount,
      joinType: joinTypeLabels[values.joinTypeKind],
      intermediarDoubled: values.intermediarDoubled,
      carcassDoubled: values.carcassDoubled,
      carcassExteriorFront: values.carcassExteriorFront,
      politeDoubled: values.politeDoubled,
      quantity: values.quantity,
      coefficient: values.coefficient || '2.5',
      carcassCoefficient: values.carcassCoefficient || '2.5',
      frontCoefficient: values.frontCoefficient || '2.5',
      backCoefficient: values.backCoefficient || '2.5',
      unitPriceNet: values.unitPriceNet,
      currency: 'RON' as const,
      notes: values.notes || undefined,
      pricingMode: 'AUTO' as const,
      materialId: values.materialId || null,
      edgeId: values.edgeId || null,
      frontMaterialId: values.frontMaterialId || null,
      frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
      backMaterialId: values.backMaterialId || null,
      backEdgeId: null,
      backHasEdge: false,
      joinTypeKind: values.joinTypeKind,
      constructionMode: values.constructionMode,
      ...piecesGeometry,
    };
    try {
      if (isEdit) {
        const updateBody: UpdateCabinetRequest = {
          ...commonBody,
          // Detaching makes the server NULL a SKU it was not sent
          // (cabinets.service.ts update). That is the right outcome for the one
          // case where `sku` goes missing here: only the from-catalog snapshot
          // can exceed 50 chars, and that snapshot IS the blueprint SKU
          // detaching exists to drop — the server nulls a within-cap one that
          // matches the blueprint for exactly the same reason. Every other edit
          // still carries its SKU, so nothing is cleared behind the user's back.
          ...(hasCatalogSourceChanges ? { detachFromCatalog: true } : {}),
          roomId: values.roomId || null,
          areaId: values.areaId || null,
          hardwareItems,
          // Undefined leaves the stored cut list untouched, which is what a
          // PARAMETRIC edit wants.
          pieces: piecePayload,
        };
        await updateMut.mutateAsync(updateBody);
      } else if (sourceCatalogItemId) {
        const body: CreateCabinetFromCatalogRequest = {
          catalogItemId: sourceCatalogItemId,
          ...(hasCatalogSourceChanges ? { detachFromCatalog: true } : {}),
          roomId: values.roomId || undefined,
          areaId: values.areaId || undefined,
          unitPriceNet: values.unitPriceNet,
          quantity: values.quantity,
          coefficient: values.coefficient || '2.5',
          carcassCoefficient: values.carcassCoefficient || '2.5',
          frontCoefficient: values.frontCoefficient || '2.5',
          backCoefficient: values.backCoefficient || '2.5',
          notes: values.notes || undefined,
          overrides: {
            name: values.name,
            sku: values.sku || undefined,
            widthMm: values.widthMm,
            heightMm: values.heightMm,
            depthMm: values.depthMm,
            intermediarCount: values.intermediarCount,
            politeCount: values.politeCount,
            joinType: joinTypeLabels[values.joinTypeKind],
            intermediarDoubled: values.intermediarDoubled,
            carcassDoubled: values.carcassDoubled,
            carcassExteriorFront: values.carcassExteriorFront,
            politeDoubled: values.politeDoubled,
            pricingMode: 'AUTO',
            materialId: values.materialId || null,
            edgeId: values.edgeId || null,
            frontMaterialId: values.frontMaterialId || null,
            frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
            backMaterialId: values.backMaterialId || null,
            backEdgeId: null,
            backHasEdge: false,
            joinTypeKind: values.joinTypeKind,
            hardwareItems,
            drawers: drawerDrafts.map((draft) => draft.body),
            constructionMode: values.constructionMode,
            pieces: piecePayload,
            ...piecesGeometry,
          },
        };
        await fromCatalogMut.mutateAsync(body);
      } else {
        const body: CreateCabinetRequest = {
          ...commonBody,
          hardwareItems: hardwareItems.length ? hardwareItems : undefined,
          drawers: drawerDrafts.length ? drawerDrafts.map((draft) => draft.body) : undefined,
          pieces: piecePayload,
        };
        await createMut.mutateAsync(body);
      }
      handleClose();
    } catch {
      /* surfaced */
    }
  };

  const submitManualPrice = async (values: ManualPriceValues) => {
    // The server computes totalNet as quantity x coefficient x unitPriceNet, so
    // both multipliers are pinned to 1 and the typed figure lands as the line
    // total unchanged. (The Custom tab defaults coefficient to 2.5 — a markup
    // that would silently inflate a price the user meant as final.)
    // Zero geometry is what marks the row as lump-sum; see isManualPriceCabinet.
    const body = {
      name: values.name,
      widthMm: 0,
      heightMm: 0,
      depthMm: 0,
      intermediarDoubled: false,
      carcassDoubled: false,
      carcassExteriorFront: false,
      politeDoubled: false,
      quantity: '1',
      coefficient: '1.00',
      unitPriceNet: values.unitPriceNet,
      currency: 'RON' as const,
      pricingMode: 'MANUAL' as const,
      constructionMode: 'PARAMETRIC' as const,
    };
    try {
      if (isEdit) {
        const updateBody: UpdateCabinetRequest = {
          ...body,
          roomId: values.roomId || null,
          areaId: values.areaId || null,
        };
        await updateMut.mutateAsync(updateBody);
      } else {
        const createBody: CreateCabinetRequest = {
          ...body,
          roomId: values.roomId || undefined,
          areaId: values.areaId || undefined,
        };
        await createMut.mutateAsync(createBody);
      }
      handleClose();
    } catch {
      /* surfaced */
    }
  };

  const saveManualToCatalog = async (values: ManualValues) => {
    if (sameCatalogName(values.name, sourceCatalogItemForSave.data?.name)) {
      manualForm.setError('name', {
        type: 'validate',
        message: t('projectItems.actions.errorSameCatalogName'),
      });
      manualForm.setFocus('name');
      return;
    }
    manualForm.clearErrors('name');
    const drawerBodies = isEdit
      ? (existingDrawers.data ?? []).map(drawerBodyFromListItem)
      : drawerDrafts.map((draft) => draft.body);
    const saved = await saveCatalogBlueprint.saveCatalogBlueprint(
      buildCabinetCatalogBody(
        values,
        drawerBodies,
        preview.data?.breakdown.subtotalNet ?? values.unitPriceNet,
        { omitSku: Boolean(sourceCatalogItemIdForSave) },
      ),
    );
    if (saved) handleClose();
  };

  const tabBtn = (id: CabinetTab, label: string) => (
    <button
      type="button"
      onClick={() => {
        if (id === 'catalog') {
          setSourceCatalogItemId(null);
          catalogForm.reset(catalogDefaults(defaultRoomId));
        } else if (id !== 'price') {
          // 'price' has its own form and no construction mode to keep in sync.
          // The mode lives in form state, not just the tab: it drives which
          // branch of the schema validates and what the submit body sends.
          manualForm.setValue('constructionMode', id === 'pieces' ? 'PIECES' : 'PARAMETRIC', {
            shouldDirty: true,
          });
        }
        setTab(id);
      }}
      className={`px-3 py-1.5 text-sm rounded-md ${
        tab === id ? 'bg-foreground text-background' : 'bg-muted text-foreground hover:bg-muted/80'
      }`}
    >
      {label}
    </button>
  );
  const createPending = sourceCatalogItemId ? fromCatalogMut.isPending : createMut.isPending;
  const createError = sourceCatalogItemId ? fromCatalogMut.error : createMut.error;
  const actionPending =
    (isEdit ? updateMut.isPending : createPending) || saveCatalogBlueprint.isPending;
  // The feronerie/cut list and the sertare of a copy arrive from two reads that
  // the summary row cannot supply. The name is focused on open, so Enter — "yes,
  // that name is fine" — lands in exactly the window where they are still in
  // flight; submitting then would save a hollow duplicate with no error shown.
  //
  // Mirrors the `enabled` on useCabinet/useDrawers: a read that is never issued
  // has nothing to wait for, and must not block the button forever.
  const copySourceEnabled = Boolean(open && isCopy && projectId && editCabinetId);
  // Gate on the data being HERE, not on the reads having merely stopped:
  // `isLoading` also goes false when a query ERRORS, both are meta:{silent:true}
  // so no toast fires either, and the seeding effects above key off `data`.
  const copySourceReady =
    !copySourceEnabled || Boolean(editCabinetQuery.data && existingDrawers.data);
  // A failed read leaves Save disabled for good, so say why in the form's own
  // error banner rather than leaving an inert button and an empty dialog.
  const copySourceError = copySourceReady
    ? null
    : (editCabinetQuery.error ?? existingDrawers.error);
  const copySourceLoading = !copySourceReady && !copySourceError;
  const submitBlocked = actionPending || !copySourceReady;
  const hasCatalogSourceChanges =
    Boolean(sourceCatalogItemIdForSave) && (manualForm.formState.isDirty || drawerDraftsDirty);
  // FIX B (2): never in copy mode. The user asked to duplicate a row, not to
  // author a blueprint, and saveManualToCatalog closes the dialog on success —
  // so offering it here doubles as a trap that discards the copy being made.
  // The copy can still be promoted from its own row once it exists.
  const canSaveToCatalog =
    !isCopy && ((!isEdit && sourceCatalogItemId === null) || hasCatalogSourceChanges);
  const visibleHardwareFields = showAllHardware
    ? hardwareFieldArray.fields
    : hardwareFieldArray.fields.slice(0, HARDWARE_PREVIEW_LIMIT);
  const hiddenHardwareCount = Math.max(
    0,
    hardwareFieldArray.fields.length - HARDWARE_PREVIEW_LIMIT,
  );
  const existingDrawerRows = sortByPosition(existingDrawers.data ?? []);
  const visibleExistingDrawerRows = showAllDrawers
    ? existingDrawerRows
    : existingDrawerRows.slice(0, DRAWER_PREVIEW_LIMIT);
  const hiddenExistingDrawerCount = Math.max(existingDrawerRows.length - DRAWER_PREVIEW_LIMIT, 0);
  const visibleDrawerDrafts = showAllDrawers
    ? drawerDrafts
    : drawerDrafts.slice(0, DRAWER_PREVIEW_LIMIT);
  const hiddenDrawerDraftCount = Math.max(drawerDrafts.length - DRAWER_PREVIEW_LIMIT, 0);

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
        <DialogContent
          className="w-[min(96vw,1100px)] max-w-none"
          // Drawers are drafted in their own dialog, outside this one, so no
          // field here sees them change; past that, the dialog's own guess.
          unsavedChanges={drawerDraftsDirty || undefined}
          // A copy opens with every field already filled, so the one thing left to
          // do is rename it: take over the dialog's open-focus and put the caret in
          // the name instead of the first control. Not selected — typing should
          // extend the pre-filled name, not wipe it. Runs after the reset effect
          // (Radix focuses on the commit that follows the content mounting).
          onOpenAutoFocus={
            isCopy
              ? (event) => {
                  event.preventDefault();
                  if (editsManualPrice) priceForm.setFocus('name');
                  else manualForm.setFocus('name');
                }
              : undefined
          }
        >
          <DialogHeader>
            <DialogTitle>
              {isCopy
                ? t('projectItems.cabinets.copyTitle')
                : isEdit
                  ? 'Editeaza corp'
                  : t('projectItems.cabinets.addTitle')}
            </DialogTitle>
            <DialogDescription>
              {isCopy
                ? t('projectItems.cabinets.copyDescription')
                : isEdit
                  ? 'Modifica toate campurile corpului, feroneria si sertarele atasate.'
                  : t('projectItems.cabinets.addDescription')}
            </DialogDescription>
          </DialogHeader>

          {!isEdit && !isCopy && (
            <div className="flex gap-2">
              {tabBtn('manual', t('projectItems.cabinets.manual'))}
              {/* "Pe piese" is not a tab any more: the switch next to the name owns
                that move, and it also carries the Custom values across. */}
              {tabBtn('catalog', t('projectItems.cabinets.fromCatalog'))}
              {tabBtn('price', t('projectItems.cabinets.manualPrice'))}
            </div>
          )}

          {!isEdit && tab === 'catalog' && (
            <Form
              advanceOnEnter
              form={catalogForm}
              onSubmit={() => undefined}
              className="space-y-3"
            >
              <RoomAreaFields projectId={projectId} roomOptions={roomOptions} />
              <CatalogItemPicker
                type="CABINET"
                registration={catalogForm.register('catalogItemId')}
                label={t('projectItems.cabinets.fields.catalogItem')}
                placeholder={t('projectItems.cabinets.fields.catalogItemPlaceholder')}
                errorMessage={catalogForm.formState.errors.catalogItemId?.message}
              />
              <p className="text-sm text-muted-foreground">
                Selecteaza un corp din catalog, apoi modifica toate campurile inainte de salvare.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={selectedCatalogItemQuery.isFetching}
                >
                  {t('common.cancel')}
                </Button>
              </DialogFooter>
            </Form>
          )}

          {(editsManualPrice || (!isEdit && tab === 'price')) && (
            <Form
              advanceOnEnter
              form={priceForm}
              onSubmit={(values) => submitManualPrice(values)}
              className="space-y-3"
            >
              <RoomAreaFields projectId={projectId} roomOptions={roomOptions} />
              <TextField name="name" label={t('projectItems.cabinets.fields.name')} />
              <TextField
                name="unitPriceNet"
                label={t('projectItems.cabinets.fields.manualPriceNet')}
                inputMode="decimal"
              />
              <p className="text-sm text-muted-foreground">
                {t('projectItems.cabinets.manualPriceHint')}
              </p>
              <FormErrorBanner
                error={isEdit ? updateMut.error : createMut.error}
                resolveKey={errorKeyFor}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={actionPending}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={actionPending} data-testid="cabinet-price-submit">
                  {(isEdit ? updateMut.isPending : createMut.isPending)
                    ? t('common.loading')
                    : t('common.submit')}
                </Button>
              </DialogFooter>
            </Form>
          )}

          {((isEdit && !editsManualPrice) || tab === 'manual' || tab === 'pieces') && (
            <Form
              advanceOnEnter
              form={manualForm}
              onSubmit={(values) => submitManual(values)}
              className="space-y-3"
            >
              <RoomAreaFields projectId={projectId} roomOptions={roomOptions} />
              {/* The name rarely needs the full width, and putting the mode switch
                beside it keeps Custom and the cut list one click apart wherever
                you are in the form. */}
              <div className="flex items-end gap-3">
                <TextField
                  name="name"
                  label={t('projectItems.cabinets.fields.name')}
                  containerClassName="min-w-0 flex-1 md:max-w-sm"
                />
                {/* Offered on an edit too — going back to detail a saved cabinet by
                  pieces is the main reason to reopen one. This form only renders
                  for a cabinet that HAS a construction to switch between; a
                  lump-sum row gets `priceForm` instead. */}
                <Button
                  type="button"
                  variant="outline"
                  className="mb-[1px] h-10 shrink-0"
                  onClick={showPieces ? switchToCustom : () => void requestSwitchToPieces()}
                  data-testid={showPieces ? 'cabinet-switch-to-custom' : 'cabinet-switch-to-pieces'}
                >
                  {showPieces
                    ? t('projectItems.cabinets.switchToCustom')
                    : t('projectItems.cabinets.switchToPieces')}
                </Button>
              </div>
              {showPieces && parametricDrifted && (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-900 dark:bg-amber-950/50"
                  data-testid="cabinet-parametric-drift"
                >
                  <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                    <AlertTriangle
                      className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
                      aria-hidden="true"
                    />
                    {t('projectItems.cabinets.applyCustomToPieces')}
                  </span>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {/* The one action the bar is arguing for, so it is the only
                      filled button on the form: solid, semibold and letter-spaced
                      apart from everything around it. */}
                    <Button
                      type="button"
                      className="h-7 px-3 text-xs font-semibold uppercase tracking-wider"
                      onClick={applyParametricToPieces}
                      data-testid="cabinet-apply-custom-to-pieces"
                    >
                      {t('projectItems.cabinets.applyCustomToPiecesConfirm')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-7 px-2 text-xs text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
                      // "Keep" is not a dismissal: it adopts what is on the Custom
                      // form as the new baseline, so the next edit asks again
                      // instead of the bar staying silent for good.
                      onClick={() => {
                        const values = manualForm.getValues();
                        setAppliedParametric({
                          spec: parametricSpecFromValues(values),
                          materials: parametricPieceMaterials(values),
                        });
                      }}
                      data-testid="cabinet-keep-pieces"
                    >
                      {t('projectItems.cabinets.applyCustomToPiecesKeep')}
                    </Button>
                  </div>
                </div>
              )}
              {showPieces ? (
                <CabinetPieceTable
                  rows={watchedRaw.pieces ?? []}
                  fields={pieceFieldArray.fields}
                  // Live, not debounced: a formula should re-measure the moment the
                  // Custom fields change, not a third of a second later.
                  dims={parametricSpecFromValues(watchedRaw)}
                  // Added simple; the row's own "Dublat" checkbox is right there,
                  // which is why this no longer stops to ask.
                  onAdd={(kind) => insertPieceRow(kind, false)}
                  onRemove={(index) => pieceFieldArray.remove(index)}
                  onDuplicate={duplicatePieceRow}
                />
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <TextField
                      name="heightMm"
                      label={t('projectItems.cabinets.fields.heightMm')}
                      {...positiveIntegerFieldProps}
                    />
                    <TextField
                      name="widthMm"
                      label={t('projectItems.cabinets.fields.widthMm')}
                      {...positiveIntegerFieldProps}
                    />
                    <TextField
                      name="depthMm"
                      label={t('projectItems.cabinets.fields.depthMm')}
                      {...positiveIntegerFieldProps}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <MaterialAutosuggest
                      name="materialId"
                      label={t('projectItems.cabinets.fields.materialId')}
                      placeholder={t('pricing.pickMaterial')}
                      usageScope="GENERAL"
                      required
                      onSelectedOption={(item) => {
                        setSelectedCarcassMaterial(item);
                        manualForm.setValue('edgeId', '', {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        // The board brings its own markup. Typing over it after
                        // is what raises the "save for next time?" question.
                        if (item?.coefficient) {
                          manualForm.setValue('carcassCoefficient', item.coefficient, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                    />
                    <MaterialAutosuggest
                      name="frontMaterialId"
                      label={t('projectItems.cabinets.fields.frontMaterialId')}
                      placeholder={t('pricing.pickMaterial')}
                      usageScope="GENERAL"
                      emptyOptionLabel={t('projectItems.cabinets.noMaterial')}
                      onSelectedOption={(item) => {
                        setSelectedFrontMaterial(item);
                        manualForm.setValue('frontEdgeId', '', {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        if (item?.coefficient) {
                          manualForm.setValue('frontCoefficient', item.coefficient, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                    />
                    <MaterialAutosuggest
                      name="backMaterialId"
                      label={t('projectItems.cabinets.fields.backMaterialId')}
                      placeholder={t('pricing.pickMaterial')}
                      usageScope="GENERAL"
                      emptyOptionLabel={t('projectItems.cabinets.noMaterial')}
                      onSelectedOption={(item) => {
                        if (item?.coefficient) {
                          manualForm.setValue('backCoefficient', item.coefficient, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                    />
                  </div>
                  {/* One markup per board, in the same three columns as the
                    pickers above so each number sits under the material it
                    prices. Seeded from the catalog when a material is picked —
                    a painted front arrives at 1 without anyone having to know
                    why. The carcass value also prices the shelves, the dividers
                    and all the banding cut with them. Typing over a seeded value
                    is what raises "make this the board's default?" on the way
                    out of the field. */}
                  <div className="grid grid-cols-3 gap-3">
                    <CoefficientField
                      name="carcassCoefficient"
                      label={t('projectItems.cabinets.fields.carcassCoefficient')}
                      target={carcassCoefficientTarget}
                      ask={coefficientPrompt.ask}
                    />
                    <CoefficientField
                      name="frontCoefficient"
                      label={t('projectItems.cabinets.fields.frontCoefficient')}
                      target={frontCoefficientTarget}
                      ask={coefficientPrompt.ask}
                    />
                    <CoefficientField
                      name="backCoefficient"
                      label={t('projectItems.cabinets.fields.backCoefficient')}
                      target={backCoefficientTarget}
                      ask={coefficientPrompt.ask}
                    />
                  </div>
                  {/* Directly under the materials, because each edge belongs to
                    the material above it: picking a carcass material clears and
                    re-resolves `edgeId` from that material's manufacturer, and
                    the front pair works the same way. The two rows are read and
                    filled as one block. */}
                  <div className="grid grid-cols-2 gap-3">
                    <EdgeAutosuggest
                      name="edgeId"
                      label={t('projectItems.cabinets.fields.edgeId')}
                      placeholder={t('pricing.pickEdge')}
                      emptyOptionLabel={t('pricing.noEdge')}
                    />
                    <EdgeAutosuggest
                      name="frontEdgeId"
                      label={t('projectItems.cabinets.fields.frontEdgeId')}
                      placeholder={t('pricing.pickEdge')}
                      emptyOptionLabel={t('pricing.noEdge')}
                    />
                  </div>
                  <CabinetDoubledCheckboxes />
                  <div className="grid grid-cols-2 gap-3">
                    <TextField
                      name="intermediarCount"
                      label={t('projectItems.cabinets.fields.intermediarCount')}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                    />
                    <TextField
                      name="politeCount"
                      label={t('projectItems.cabinets.fields.politeCount')}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                    />
                  </div>
                </>
              )}
              {/* No cabinet-wide coefficient here any more: the markup lives on
                each board above (and, for a cut list, on each row). Leaving the
                field would give the user a number that multiplies nothing. */}
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  name="quantity"
                  label={t('projectItems.cabinets.fields.quantity')}
                  {...positiveIntegerFieldProps}
                />
                <SelectField
                  name="joinTypeKind"
                  label={t('projectItems.cabinets.fields.joinTypeKind')}
                  options={[
                    { value: 'EUROSCREW', label: 'Eurosurub' },
                    { value: 'DEMONTABIL', label: 'Demontabil' },
                    { value: 'CLAMEX', label: 'Clamex' },
                    { value: 'MITER_45', label: '45°' },
                    { value: 'NONE', label: 'None' },
                  ]}
                />
              </div>
              <fieldset className="space-y-3 rounded-md border border-border p-3">
                <legend className="px-1 text-sm font-medium text-foreground">
                  {t('projectItems.cabinets.hardware.selected')}
                </legend>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_7rem_auto]">
                  <HardwareAutosuggest
                    key={hardwarePickerKey}
                    name="hardwareCatalogId"
                    label={t('projectItems.cabinets.fields.hardwareCatalogId')}
                    placeholder={t('pricing.pickHardware')}
                    onSelectedOption={setHardwareCandidate}
                  />
                  <TextField
                    name="hardwareQuantity"
                    label={t('projectItems.cabinets.fields.hardwareQuantity')}
                    {...positiveIntegerFieldProps}
                    className="h-10"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="self-end"
                    onClick={() => void addSelectedHardware()}
                    disabled={!hardwareCandidate}
                  >
                    {t('projectItems.cabinets.hardware.add')}
                  </Button>
                </div>
                {hardwareFieldArray.fields.length > 0 ? (
                  <div className="space-y-2">
                    {visibleHardwareFields.map((field, index) => (
                      <HardwareItemRow
                        key={field.id}
                        index={index}
                        hardware={watchedRaw.hardwareItems?.[index] ?? field}
                        onRemove={() => hardwareFieldArray.remove(index)}
                        onReplace={(item) => replaceHardwareItem(index, item)}
                        askCoefficient={coefficientPrompt.ask}
                      />
                    ))}
                    {hiddenHardwareCount > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowAllHardware((current) => !current)}
                      >
                        {showAllHardware ? 'View less' : `View more (${hiddenHardwareCount})`}
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('projectItems.cabinets.hardware.empty')}
                  </p>
                )}
              </fieldset>
              <fieldset className="space-y-3 rounded-md border border-border p-3">
                <legend className="px-1 text-sm font-medium text-foreground">Sertare</legend>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {isEdit
                      ? 'Gestioneaza sertarele atasate acestui corp.'
                      : 'Adauga sertare inainte de salvarea corpului; acestea vor fi create impreuna cu corpul.'}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    // Same gate as Save. A copy seeds its drafts from the source's
                    // sertare when that read lands, and the seeding effect bails
                    // for good once the drafts are dirty — so a draft added while
                    // the read is still in flight would silently cost the copy
                    // every sertar the source had.
                    disabled={submitBlocked}
                    onClick={() => {
                      if (isEdit) {
                        setEditingExistingDrawer(null);
                        setShowAddExistingDrawer(true);
                        // The new drawer will be appended, so make sure the list
                        // is open to show it when it lands.
                        setShowAllDrawers(true);
                      } else {
                        setEditingDrawerDraft(null);
                        setDrawerDraftDialogOpen(true);
                      }
                    }}
                  >
                    Adauga sertar
                  </Button>
                </div>
                {((isEdit && existingDrawers.isLoading) || copySourceLoading) && (
                  <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                )}
                {isEdit && !existingDrawers.isLoading && existingDrawerRows.length > 0 && (
                  <div className="space-y-1">
                    {visibleExistingDrawerRows.map((drawer) => {
                      const drawerTotalNet = drawerLineTotalNet(drawer);
                      return (
                        <div
                          key={drawer.id}
                          className="grid gap-1.5 rounded-md border border-border bg-card p-1.5 md:grid-cols-[minmax(0,1fr)_auto]"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {drawer.name || 'Sertar'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {[drawer.drawerModel, drawer.drawerType, drawer.slideModel]
                                .filter(Boolean)
                                .join(' / ')}
                              {drawer.hBox ? ` · H-box ${drawer.hBox}` : ''}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {drawer.heightMm} x {drawer.widthMm} x {drawer.depthMm} mm · Bucati{' '}
                              {drawer.quantity} · Coef. {drawer.coefficient}
                            </p>
                            <p className="text-xs font-medium text-foreground">
                              Pret sertar: {formatCurrencyValue(drawerTotalNet)}
                            </p>
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => setEditingExistingDrawer(drawer)}
                            >
                              Editeaza
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => setPendingExistingDrawerDelete(drawer)}
                            >
                              {t('common.delete')}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {hiddenExistingDrawerCount > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowAllDrawers((current) => !current)}
                      >
                        {showAllDrawers ? 'View less' : `View more (${hiddenExistingDrawerCount})`}
                      </Button>
                    )}
                  </div>
                )}
                {isEdit &&
                  !existingDrawers.isLoading &&
                  (existingDrawers.data?.length ?? 0) === 0 && (
                    <p className="text-sm text-muted-foreground">Nu ai adaugat inca sertare.</p>
                  )}
                {!isEdit && drawerDrafts.length > 0 ? (
                  <div className="space-y-1">
                    {visibleDrawerDrafts.map((draft) => {
                      const drawerTotalNet = drawerLineTotalNet(draft.body);
                      return (
                        <div
                          key={draft.id}
                          className="grid gap-1.5 rounded-md border border-border bg-card p-1.5 md:grid-cols-[minmax(0,1fr)_auto]"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {draftNames.get(draft.id)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {[
                                draft.body.drawerModel,
                                draft.body.drawerType,
                                draft.body.slideModel,
                              ]
                                .filter(Boolean)
                                .join(' / ')}
                              {draft.body.hBox ? ` · H-box ${draft.body.hBox}` : ''}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {draft.body.heightMm} x {draft.body.widthMm} x {draft.body.depthMm} mm
                              · Bucati {draft.body.quantity} · Coef. {draft.body.coefficient}
                            </p>
                            <p className="text-xs font-medium text-foreground">
                              Pret sertar: {formatCurrencyValue(drawerTotalNet)}
                            </p>
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setEditingDrawerDraft(draft);
                                setDrawerDraftDialogOpen(true);
                              }}
                            >
                              Editeaza
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setDrawerDrafts((current) =>
                                  current.filter((item) => item.id !== draft.id),
                                );
                                setDrawerDraftsDirty(true);
                              }}
                            >
                              {t('common.delete')}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {hiddenDrawerDraftCount > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowAllDrawers((current) => !current)}
                      >
                        {showAllDrawers ? 'View less' : `View more (${hiddenDrawerDraftCount})`}
                      </Button>
                    )}
                  </div>
                ) : !isEdit && copySourceReady ? (
                  <p className="text-sm text-muted-foreground">Nu ai adaugat inca sertare.</p>
                ) : null}
              </fieldset>
              <div className="space-y-3 rounded-md border border-border p-3">
                <BreakdownPanel
                  breakdown={preview.data?.breakdown ?? null}
                  loading={preview.isFetching}
                  error={preview.error}
                />
                {drawerPricingSummary.rows.length > 0 && (
                  <div className="rounded-md border border-border bg-muted/20 p-3 text-xs">
                    <div className="mb-2 font-medium text-foreground">
                      Sertare incluse in calcul
                    </div>
                    <ul className="space-y-1">
                      {drawerPricingSummary.rows.map((row) => (
                        <li key={row.id} className="flex justify-between gap-3">
                          <span className="truncate text-muted-foreground">{row.name}</span>
                          <span className="tabular-nums">{formatCurrencyValue(row.totalNet)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 space-y-1 border-t border-border pt-2">
                      <div className="flex justify-between">
                        <span>Total sertare</span>
                        <span className="tabular-nums">
                          {formatCurrencyValue(drawerPricingSummary.totalNet)}
                        </span>
                      </div>
                      {combinedPreviewTotalNet !== null && (
                        <div className="flex justify-between font-semibold text-foreground">
                          <span>Total corp + sertare</span>
                          <span className="tabular-nums">
                            {formatCurrencyValue(combinedPreviewTotalNet)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <TextField name="notes" label={t('projectItems.cabinets.fields.notes')} />
              <FormErrorBanner
                // A source read that failed takes precedence: it is why Save is
                // disabled, so nothing else can be attempted until it is retried.
                error={isEdit ? updateMut.error : (copySourceError ?? createError)}
                resolveKey={errorKeyFor}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={actionPending}
                >
                  {t('common.cancel')}
                </Button>
                {canSaveToCatalog && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={manualForm.handleSubmit(saveManualToCatalog)}
                    disabled={submitBlocked}
                  >
                    {saveCatalogBlueprint.isPending
                      ? t('common.loading')
                      : t('projectItems.actions.saveToCatalog')}
                  </Button>
                )}
                <Button type="submit" disabled={submitBlocked} data-testid="cabinet-submit">
                  {(isEdit ? updateMut.isPending : createPending)
                    ? t('common.loading')
                    : t('common.submit')}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </DialogContent>
        {(drawerDraftDialogOpen || editingDrawerDraft) && (
          <AddDrawerDialog
            projectId={projectId}
            draft={editingDrawerDraft?.body ?? null}
            // A new draft goes to the bottom of the stack; an edited one keeps
            // the slot it already occupies.
            stackPosition={
              editingDrawerDraft
                ? {
                    index: Math.max(
                      0,
                      drawerDrafts.findIndex((draft) => draft.id === editingDrawerDraft.id),
                    ),
                    total: drawerDrafts.length,
                  }
                : { index: drawerDrafts.length, total: drawerDrafts.length + 1 }
            }
            open={drawerDraftDialogOpen}
            onClose={closeDrawerDraftDialog}
            onSaveDraft={saveDrawerDraft}
          />
        )}
        {isEdit && (showAddExistingDrawer || editingExistingDrawer) && (
          <AddDrawerDialog
            projectId={projectId}
            cabinetId={editCabinetId}
            drawer={editingExistingDrawer}
            // Saved drawers are appended, so a new one goes to the bottom.
            stackPosition={drawerStackPosition(existingDrawers.data, editingExistingDrawer)}
            open={showAddExistingDrawer || editingExistingDrawer !== null}
            onClose={() => {
              setShowAddExistingDrawer(false);
              setEditingExistingDrawer(null);
            }}
          />
        )}
        <ConfirmDialog
          open={pendingExistingDrawerDelete !== null}
          title="Sterge sertar"
          description={
            pendingExistingDrawerDelete
              ? `Sigur vrei sa stergi sertarul "${pendingExistingDrawerDelete.name ?? '—'}"?`
              : ''
          }
          confirmLabel={t('common.delete')}
          variant="destructive"
          loading={deleteDrawerMut.isPending}
          onCancel={() => setPendingExistingDrawerDelete(null)}
          onConfirm={async () => {
            if (!pendingExistingDrawerDelete) return;
            try {
              await deleteDrawerMut.mutateAsync(pendingExistingDrawerDelete.id);
            } finally {
              setPendingExistingDrawerDelete(null);
            }
          }}
        />
      </Dialog>
      {/* A sibling, never a child. Two Radix dialogs nested share one scroll
          lock, and the inner one leaves `pointer-events: none` on <body> even
          while closed — which silently swallows every click in the dialog
          above it. It also has to outlive the close: the question is about the
          cabinet that was just saved. */}
      {coefficientPrompt.dialog}
    </>
  );
}
